import { useTranslation } from 'react-i18next';
import { BookOpen } from 'lucide-react';
import { cn } from '../../lib/cn';

const sourceLabels = {
  wordnet: 'WordNet',
  wiktionary: 'Wiktionary',
  curated: 'Curated',
  ai_simplified: 'AI simplified',
};

const levelLabels = {
  elementary: 'Elementary',
  middle: 'Middle',
  high: 'High',
};

const levelOrder = ['elementary', 'middle', 'high'];

const levelPillStyles = {
  elementary: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  middle: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  high: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
};

const domainColors = {
  academic: 'text-blue-600 dark:text-blue-400',
  medical: 'text-red-600 dark:text-red-400',
  legal: 'text-purple-600 dark:text-purple-400',
  technical: 'text-orange-600 dark:text-orange-400',
  scientific: 'text-green-600 dark:text-green-400',
  literary: 'text-pink-600 dark:text-pink-400',
  informal: 'text-gray-600 dark:text-gray-400',
  general: 'text-gray-600 dark:text-gray-400',
};

/**
 * Renders dictionary definitions in one of two layouts:
 *  - Flat (default): one ordered list of definitions. Used wherever level
 *    grouping isn't relevant (e.g. student dictionary view, which has
 *    already been filtered server-side to the student's level).
 *  - Grouped: one card per sense, with level variants stacked inside each
 *    card. Used on the teacher dictionary detail page so curators can
 *    scan elementary/middle/high side-by-side and judge whether AI
 *    simplifications stayed faithful to the canonical text. Triggered by
 *    passing the `groups` prop (from the teacher controller's
 *    `definitionGroups` payload).
 */
export default function DefinitionList({
  definitions,
  groups,
  level = 'high',
  showSource = true,
  className,
}) {
  const { t } = useTranslation();

  if (Array.isArray(groups) && groups.length > 0) {
    return <GroupedDefinitions groups={groups} level={level} className={className} />;
  }

  if (!definitions || definitions.length === 0) {
    return (
      <p className="text-gray-500 dark:text-gray-400 italic">
        {t('dictionary.noDefinitions')}
      </p>
    );
  }

  return (
    <ol className={cn('space-y-4', className)}>
      {definitions.map((def, idx) => (
        <li key={def.id || idx} className="flex gap-3">
          <span className="text-gray-400 dark:text-gray-500 font-medium flex-shrink-0">
            {idx + 1}.
          </span>
          <div className="flex-1">
            {def.matchesContext && <ContextMatchBadge />}
            <DefinitionBody def={def} showSource={showSource} />
          </div>
        </li>
      ))}
    </ol>
  );
}

// Which reading levels have at least one definition across all senses. High is
// effectively always present (the canonical row); elementary/middle only after
// generation. Drives the greyed-out toggle options.
export function availableDefinitionLevels(groups) {
  const set = new Set();
  (groups || []).forEach((g) => (g.levels || []).forEach((d) => set.add(d.readingLevel)));
  return set;
}

// Pick the definition to show for a sense at the requested level, walking UP
// the ladder (elementary -> middle -> high) when that sense has no variant at
// the requested level — so a sense that only has "high" still shows high when
// the teacher is viewing "elementary".
function pickForLevel(levels, level) {
  const start = levelOrder.indexOf(level);
  const ladder = start >= 0 ? levelOrder.slice(start) : levelOrder;
  for (const lvl of ladder) {
    const match = (levels || []).find((d) => d.readingLevel === lvl);
    if (match) return match;
  }
  return sortLevels(levels)[0] || null;
}

// Three-way reading-level toggle. Levels with no generation are disabled.
export function DefinitionLevelToggle({ value, onChange, available }) {
  return (
    <div className="inline-flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden text-xs">
      {levelOrder.map((lvl, i) => {
        const enabled = lvl === 'high' || (available && available.has(lvl));
        const selected = value === lvl;
        return (
          <button
            key={lvl}
            type="button"
            disabled={!enabled}
            onClick={() => enabled && onChange(lvl)}
            title={enabled ? undefined : `No ${levelLabels[lvl].toLowerCase()} definitions yet`}
            className={cn(
              'px-2.5 py-1 font-medium transition-colors',
              i > 0 && 'border-l border-gray-200 dark:border-gray-700',
              !enabled && 'text-gray-300 dark:text-gray-600 cursor-not-allowed',
              enabled && !selected && 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800',
              selected && 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
            )}
          >
            {levelLabels[lvl]}
          </button>
        );
      })}
    </div>
  );
}

function GroupedDefinitions({ groups, level = 'high', className }) {
  return (
    <ol className={cn('space-y-5', className)}>
      {groups.map((group, idx) => {
        const def = pickForLevel(group.levels, level);
        if (!def) return null;
        return (
          <li
            key={group.senseId || idx}
            className="rounded-md border border-gray-200 dark:border-gray-700 p-3 bg-white/40 dark:bg-gray-900/40"
          >
            <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
              Sense {idx + 1}
            </div>
            <div className="flex gap-3">
              <LevelPill level={def.readingLevel} />
              <div className="flex-1">
                <DefinitionBody def={def} showSource />
                {def.source === 'ai_simplified' && def.originalText && (
                  <details className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    <summary className="cursor-pointer select-none">
                      Canonical (high-level) source
                    </summary>
                    <p className="mt-1 italic">{def.originalText}</p>
                  </details>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// Marks the sense the student's own captured sentence uses. The server already
// sorts this definition to the front (see Student::DictionaryController
// #lead_with_matched_sense) — the badge says *why* it leads.
function ContextMatchBadge() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1 mb-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-900 text-white dark:bg-white dark:text-gray-900">
      <BookOpen className="w-3 h-3" aria-hidden="true" />
      {t('dictionary.matchesYourContext', 'The meaning in your sentence')}
    </span>
  );
}

function DefinitionBody({ def, showSource }) {
  return (
    <>
      <p className="text-gray-800 dark:text-gray-200">{def.text}</p>
      {def.example && (
        <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-1">
          "{def.example}"
          {def.exampleAttribution && (
            <span className="not-italic text-gray-400 dark:text-gray-500">
              {' '}&#x2014; {def.exampleAttribution}
            </span>
          )}
        </p>
      )}
      <div className="flex items-center gap-2 mt-1">
        {showSource && def.source && (
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {sourceLabels[def.source] || def.source}
          </span>
        )}
        {def.domain && def.domain !== 'general' && (
          <span className={cn('text-xs font-medium', domainColors[def.domain])}>
            [{def.domain}]
          </span>
        )}
      </div>
    </>
  );
}

function LevelPill({ level }) {
  const label = levelLabels[level] || level;
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-medium h-fit flex-shrink-0',
        levelPillStyles[level] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      )}
    >
      {label}
    </span>
  );
}

function sortLevels(levels) {
  return [...(levels || [])].sort(
    (a, b) => levelOrder.indexOf(a.readingLevel) - levelOrder.indexOf(b.readingLevel),
  );
}
